import { computed } from 'vue'
import {
  type AdminPermission,
  type OrgPermission,
  type Permission,
  type PermissionGroup,
  adminPermissionTypes,
  orgPermissionTypes,
  state
} from '../state'
import type { OrganizationPermissionGroups } from './authentication-service'

export function mapAdminPermissions(
  permissionGroupIds: string[],
  permissionGroups: PermissionGroup[]
): Set<AdminPermission> {
  const adminPermissions = permissionGroupIds
    .map(groupId => permissionGroups.find(g => g.id === groupId))
    .filter((group): group is PermissionGroup => group !== undefined && !group.isOrganizationLevel)
    .flatMap(group => group.permissions)
    .filter(isAdminPermission)

  return new Set(adminPermissions)
}

export function mapOrganizationPermissions(
  organizationPermissionGroups: OrganizationPermissionGroups[],
  permissionGroups: PermissionGroup[]
): { [orgId: string]: Set<OrgPermission> } {
  return organizationPermissionGroups.reduce(
    (acc, orgPerm) => {
      const orgPermissions = orgPerm.permissionGroupIds
        .map(groupId => permissionGroups.find(g => g.id === groupId))
        .filter((group): group is PermissionGroup => group !== undefined && group.isOrganizationLevel)
        .flatMap(group => group.permissions)
        .filter(isOrgPermission)

      acc[orgPerm.organizationId] = new Set(orgPermissions)

      return acc
    },
    {} as { [orgId: string]: Set<OrgPermission> }
  )
}

function isAdminPermission(permission: Permission): permission is AdminPermission {
  return adminPermissionTypes.includes(permission as AdminPermission)
}

function isOrgPermission(permission: Permission): permission is OrgPermission {
  return orgPermissionTypes.includes(permission as OrgPermission)
}

export const isAdmin = computed(() => state.adminPermissions.has('Admin.BasicAccess'))
export const canEditTechniques = computed(() => state.adminPermissions.has('PlayerTechniques.Edit'))
export const canEditPlayerBuilds = computed(() => state.adminPermissions.has('PlayerBuilds.Edit'))

export function hasAdminPermission(permission: AdminPermission) {
  return state.adminPermissions.has(permission)
}

const orgPermissions = computed(() => state.organizationPermissions[state.organizationId!] ?? new Set())

export function hasOrgPermission(permission: OrgPermission) {
  return state.adminPermissions.has('Organizations.AllPermissions') || orgPermissions.value.has(permission)
}
